<script setup lang="ts">
  import { onBeforeMount, ref } from 'vue';
  import { RouterLink } from 'vue-router';

  import type { CreditMemo, Partner } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { Sheet, SheetContent } from '@ui/components/sheet';

  import { stuut } from '@/api';
  import CreditMemoDetailed from '@/components/tasks/CreditMemoDetailed.vue';

  interface Props {
    creditMemo: CreditMemo;
    hideCloseBtn?: boolean;
    partner?: Partner;
  }

  const props = withDefaults(defineProps<Props>(), { hideCloseBtn: true });
  const emits = defineEmits<{
    (e: 'close'): void;
  }>();

  const creditMemo = ref<CreditMemo>(props.creditMemo);
  const partner = ref<Partner | undefined>(props.partner);

  onBeforeMount(async () => {
    if (!props.partner?.id) {
      const partnerId = props.creditMemo?.partner?.id || props.creditMemo?.partner_id;
      if (partnerId) {
        partner.value = await stuut.partners.retrieve(partnerId);
      }
    }

    if (!props.creditMemo?.line_items?.length && props.creditMemo?.id) {
      creditMemo.value = await stuut.creditMemos.retrieve(props.creditMemo.id);
    }
  });
</script>

<template>
  <Sheet v-bind="$attrs" :key="creditMemo.id">
    <SheetContent
      class="w-full md:w-150 bg-secondary p-0"
      :hide-close-btn="hideCloseBtn"
      @close="emits('close')"
    >
      <Button
        class="absolute top-0 right-4.5"
        variant="ghost"
        size="icon-sm"
        @click="emits('close')"
      >
        <i class="fas fa-close" />
      </Button>

      <div class="px-6 py-8 h-full overflow-y-auto space-y-4">
        <CreditMemoDetailed :credit-memo="creditMemo" :partner="partner" />

        <div class="flex justify-between px-2">
          <RouterLink
            :to="{
              name: 'credit-memos.credit-memo',
              params: { id: props.creditMemo?.id },
            }"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button class="font-bold" size="inline" variant="link"> View Credit Memo </Button>
          </RouterLink>

          <RouterLink
            v-if="partner?.id"
            :to="{
              name: 'customers.customer.credit-memos',
              params: { id: partner?.id },
            }"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button class="font-bold" size="inline" variant="link">
              View {{ partner.name || partner.computedName }} credit memos
            </Button>
          </RouterLink>
        </div>
      </div>
    </SheetContent>
  </Sheet>
</template>
